import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { Section } from '../persistence/localProgress';

type TotalsBySection = Record<Section, number>;
type AnsweredBySection = Partial<Record<Section, number>>;

type SurveyProgressBarProps = {
  totals: TotalsBySection;
  answered: AnsweredBySection;
  className?: string;
  onBack?: () => void;
  onForward?: () => void;
  canGoBack?: boolean;
  canGoForward?: boolean;
};

const SECTION_LABEL: Record<Section, string> = {
  personality: 'Personality',
  interests: 'Interests',
  values: 'Values',
};

export default function SurveyProgressBar({ totals, answered, className, onBack, onForward, canGoBack, canGoForward }: SurveyProgressBarProps) {
  const grandTotal = (Object.keys(totals) as Section[]).reduce((sum, key) => sum + (totals[key] || 0), 0);
  if (!grandTotal) return null;

  return (
    <div className={['w-full max-w-3xl px-4 sm:px-6', className || ''].join(' ')}>
      <div className="flex items-start justify-between w-full gap-3 sm:gap-4">
        <button
          type="button"
          aria-label="Previous question"
          onClick={onBack}
          disabled={!onBack || !canGoBack}
          className="text-sm font-semibold text-white rounded-full hover:text-white/80 disabled:opacity-50"
        >
          <ChevronLeftIcon className="w-4 h-4" />
        </button>

        <div className="flex-1">
          <div className="relative flex h-3 rounded-full overflow-hidden bg-white/20 w-full">
            {(Object.keys(totals) as Section[]).map((section) => {
              const total = totals[section] || 0;
              const done = Math.min(answered[section] || 0, total);
              const pct = total > 0 ? Math.round((done / total) * 100) : 0;
              return (
                <div key={section} className="relative bg-white/10" style={{ flexGrow: total, flexBasis: 0 }}>
                  <div className="relative z-10 h-full bg-primary-300" style={{ width: `${pct}%` }} />
                </div>
              );
            })}

            {(() => {
              const sections = Object.keys(totals) as Section[];
              let acc = 0;
              const seps: JSX.Element[] = [];
              sections.forEach((s, idx) => {
                const w = totals[s] || 0;
                acc += w;
                if (idx < sections.length - 1) {
                  const leftPct = (acc / grandTotal) * 100;
                  seps.push(
                    <div
                      key={`sep-${s}`}
                      className="absolute z-0 top-0 h-full w-[2px] bg-white/60"
                      style={{ left: `${leftPct}%`, transform: 'translateX(-1px)' }}
                    />,
                  );
                }
              });
              return seps;
            })()}
          </div>

          <div className="flex justify-between gap-2 mt-2 text-white/90 text-[10px] sm:text-xs">
            {(Object.keys(totals) as Section[]).map((section) => (
              <div key={section} className="text-center" style={{ flexGrow: totals[section] || 0, flexBasis: 0 }}>
                {SECTION_LABEL[section]}
              </div>
            ))}
          </div>
        </div>

        <button
          type="button"
          aria-label="Next question"
          onClick={onForward}
          disabled={!onForward || !canGoForward}
          className="text-sm font-semibold text-white rounded-full hover:text-white/80 disabled:opacity-50"
        >
          <ChevronRightIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
